import {
  applyFeatureDependencies,
  conflictingFeatures,
  missingFeatureDependencies,
} from "@school-sales-ai/shared";
import type { CreatePlanBody, ManagePlan, PlanFeatureOption, UpdatePlanBody } from "@/lib/api";

// Form state for the Planes editor. `position` is a string so the numeric input
// can be empty (→ backend assigns the next position on create).
export type PlanFormValues = {
  key: string;
  name: string;
  position: string;
  featureIds: string[];
};

export type PlanFormErrors = Partial<Record<"key" | "name" | "position", string>>;

export const EMPTY_PLAN_FORM: PlanFormValues = {
  key: "",
  name: "",
  position: "",
  featureIds: [],
};

export function planToFormValues(plan: ManagePlan): PlanFormValues {
  return {
    key: plan.key,
    name: plan.name,
    position: String(plan.position),
    featureIds: plan.featureIds,
  };
}

const KEY_RE = /^[a-z0-9_]+$/;

// `isEdit` skips key validation — the key is immutable and shown read-only.
export function validatePlanForm(
  values: PlanFormValues,
  isEdit: boolean,
): PlanFormErrors {
  const errors: PlanFormErrors = {};

  if (!isEdit) {
    const key = values.key.trim();
    if (key.length < 2) {
      errors.key = "La clave es obligatoria (mínimo 2 caracteres).";
    } else if (key.length > 32 || !KEY_RE.test(key)) {
      errors.key = "Solo minúsculas, números y guiones bajos (máx. 32).";
    }
  }

  const name = values.name.trim();
  if (name.length < 2) {
    errors.name = "El nombre es obligatorio (mínimo 2 caracteres).";
  } else if (name.length > 64) {
    errors.name = "El nombre no puede exceder 64 caracteres.";
  }

  if (values.position.trim() !== "") {
    const n = Number(values.position);
    if (!Number.isInteger(n) || n < 0) {
      errors.position = "La posición debe ser un entero mayor o igual a 0.";
    }
  }

  return errors;
}

function parsedPosition(values: PlanFormValues): number | undefined {
  return values.position.trim() === "" ? undefined : Number(values.position);
}

export function toCreateBody(values: PlanFormValues): CreatePlanBody {
  return {
    key: values.key.trim(),
    name: values.name.trim(),
    position: parsedPosition(values),
    featureIds: values.featureIds,
  };
}

export function toUpdateBody(values: PlanFormValues): UpdatePlanBody {
  return {
    name: values.name.trim(),
    position: parsedPosition(values),
    featureIds: values.featureIds,
  };
}

function keysOf(featureIds: readonly string[], features: readonly PlanFeatureOption[]): Set<string> {
  return new Set(features.filter((f) => featureIds.includes(f.id)).map((f) => f.key));
}

/**
 * The names of the features `feature` needs and the form does not have ticked
 * (e.g. «Pedidos» for «Envío a domicilio»). Empty when it may be ticked. The
 * dependency table is `FEATURE_DEPENDENCIES` in packages/shared, the same one
 * the API enforces — so the checkbox and the 400 cannot disagree.
 */
export function missingPlanFeatureNames(
  feature: PlanFeatureOption,
  featureIds: readonly string[],
  features: readonly PlanFeatureOption[],
): string[] {
  return missingFeatureDependencies(feature.key, keysOf(featureIds, features)).map(
    (key) => features.find((f) => f.key === key)?.name ?? key,
  );
}

/**
 * The names of the ticked features `feature` cannot be granted alongside (e.g.
 * «Pedidos» for «Solicitudes de cotización»). Empty when it may be ticked. Read
 * from `FEATURE_CONFLICTS`, the table the API enforces.
 */
export function conflictingPlanFeatureNames(
  feature: PlanFeatureOption,
  featureIds: readonly string[],
  features: readonly PlanFeatureOption[],
): string[] {
  return conflictingFeatures(feature.key, keysOf(featureIds, features)).map(
    (key) => features.find((f) => f.key === key)?.name ?? key,
  );
}

/**
 * The next `featureIds` after clicking one checkbox.
 *
 * Ticking a feature whose prerequisite is missing does nothing (its checkbox is
 * disabled anyway). Unticking a prerequisite also unticks everything that
 * depends on it — leaving «Envío a domicilio» ticked under no «Pedidos» would
 * only turn the Save button into a 400.
 */
export function toggleFeatureId(
  featureIds: readonly string[],
  id: string,
  features: readonly PlanFeatureOption[],
): string[] {
  const feature = features.find((f) => f.id === id);
  if (featureIds.includes(id)) {
    const remaining = featureIds.filter((f) => f !== id);
    const kept = applyFeatureDependencies(keysOf(remaining, features));
    return remaining.filter((fid) => {
      const key = features.find((f) => f.id === fid)?.key;
      // An id the catalog no longer lists is left alone: nothing here can judge it.
      return key === undefined || kept.has(key);
    });
  }
  if (feature && missingPlanFeatureNames(feature, featureIds, features).length > 0) {
    return [...featureIds];
  }
  // A conflicting pair is refused the same way: the checkbox is disabled, and a
  // click that reaches here anyway changes nothing.
  if (feature && conflictingPlanFeatureNames(feature, featureIds, features).length > 0) {
    return [...featureIds];
  }
  return [...featureIds, id];
}
